import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconPlus, IconLoader, IconAlert, IconTrash } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

interface StockCountRow {
  id: string;
  warehouse_id: string;
  warehouse_name: string;
  status: string;
  note: string | null;
  started_at: string;
  finished_at: string | null;
  item_count: number;
  total_value_diff: number | null;
}

interface Warehouse { id: string; name: string; }

interface StockCountPage {
  items: StockCountRow[];
  total: number;
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleString("cs-CZ") : "—");
const fmtMoney = (v: number | null) => (v === null ? "—" : `${(v / 100).toFixed(2)} Kč`);

export default function StockCounts() {
  const { user } = useCurrentUser();
  const canWrite = can(user, "stock_counts_perform");
  const { t } = useTranslation();
  const nav = useNavigate();
  const [counts, setCounts] = useState<StockCountRow[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pageIndex, setPageIndex] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String(pageIndex * pageSize),
    });
    api.get<StockCountPage>(`/stock-counts?${params}`)
      .then(({ data }) => { setCounts(data.items); setTotal(data.total); })
      .catch(() => setErr(t("stockCount.loadError")))
      .finally(() => setLoading(false));
  }, [pageSize, pageIndex, t]);

  useEffect(() => {
    api.get<Warehouse[]>("/warehouses").then(({ data }) => {
      setWarehouses(data);
      if (data.length === 1) setWarehouseId(data[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPageIndex(0); }, [pageSize]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  async function create() {
    if (!warehouseId) { setErr(t("stockCount.warehouseRequired")); return; }
    setCreating(true);
    setErr(null);
    try {
      const { data } = await api.post<StockCountRow>("/stock-counts", {
        warehouse_id: warehouseId,
        note: note || null,
      });
      nav(`/stock-counts/${data.id}`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setErr(detail === "OPEN_COUNT_EXISTS" ? t("stockCount.openExists") : (detail || t("stockCount.saveError")));
    } finally {
      setCreating(false);
    }
  }

  async function remove(row: StockCountRow) {
    if (!confirm(t("stockCount.deleteConfirm"))) return;
    try {
      await api.delete(`/stock-counts/${row.id}`);
      load();
    } catch (e: any) {
      setErr(apiError(e, t("stockCount.saveError")));
    }
  }

  if (loading && counts.length === 0) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>{t("stockCount.title")}</h1>
        <p>{t("stockCount.subtitle")}</p>
      </div>

      {err && <div className="card" style={{ padding: 12, marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}><IconAlert size={16} /> {err}</div>}

      <div className="card" style={{ padding: 20, marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ minWidth: 240, flex: 1 }}>
          <label>{t("stockCount.warehouse")} *</label>
          <SearchableSelect value={warehouseId} onChange={setWarehouseId} options={warehouses.map(w => ({ value: w.id, label: w.name }))} placeholder={t("stockCount.selectWarehouse")} />
        </div>
        <div style={{ minWidth: 240, flex: 2 }}>
          <label>{t("stockCount.note")}</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder={t("stockCount.notePlaceholder")} />
        </div>
        {canWrite && (
        <button className="btn-primary" onClick={create} disabled={creating} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <IconPlus size={18} /> {creating ? t("common.saving") : t("stockCount.new")}
        </button>
        )}
      </div>

      {counts.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--muted)" }}>{t("stockCount.empty")}</div>
      ) : (
        <div className="card" style={{ overflow: "auto", opacity: loading ? 0.6 : 1 }}>
          <table>
            <thead>
              <tr>
                <th>{t("stockCount.startedAt")}</th>
                <th>{t("stockCount.finishedAt")}</th>
                <th>{t("stockCount.warehouse")}</th>
                <th>{t("stockCount.note")}</th>
                <th>{t("stockCount.itemCount")}</th>
                <th>{t("stockCount.totalDiff")}</th>
                <th>{t("common.status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {counts.map((c) => (
                <tr key={c.id}>
                  <td>{fmt(c.started_at)}</td>
                  <td>{fmt(c.finished_at)}</td>
                  <td>{c.warehouse_name}</td>
                  <td style={{ color: "var(--muted)" }}>{c.note || "—"}</td>
                  <td>{c.item_count}</td>
                  <td style={{
                    fontWeight: 600,
                    color: c.total_value_diff === null ? "var(--muted)" : c.total_value_diff < 0 ? "var(--red)" : c.total_value_diff > 0 ? "var(--green)" : "var(--text)",
                  }}>{fmtMoney(c.total_value_diff)}</td>
                  <td>
                    <span style={{
                      padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: c.status === "open" ? "var(--accent-dim)" : "var(--green-dim)",
                      color: c.status === "open" ? "var(--accent)" : "var(--green)",
                    }}>{t(`stockCount.status.${c.status}`)}</span>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Link to={`/stock-counts/${c.id}`} className="ghost" style={{ fontSize: 13 }}>
                      {c.status === "open" ? t("stockCount.continue") : t("stockCount.view")}
                    </Link>
                    {c.status === "open" && canWrite && (
                      <button className="ghost" onClick={() => remove(c)} style={{ color: "var(--red)" }} title={t("common.delete")}>
                        <IconTrash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
              <span>{t("stockCount.perPage")}</span>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: "auto" }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>· {t("stockCount.total", { total })}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <button className="ghost" disabled={pageIndex === 0} onClick={() => setPageIndex(i => i - 1)}>‹ {t("stockCount.prev")}</button>
              <span style={{ color: "var(--muted)" }}>{pageIndex + 1} / {pageCount}</span>
              <button className="ghost" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(i => i + 1)}>{t("stockCount.next")} ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
